/**
 * Debug mode utilities
 * 
 * Stores events in localStorage (last 20 events across pages) instead of console logging.
 * History auto-displays on page load when debug is enabled.
 * 
 * Usage:
 * - Enable: Tracking.setDebugMode(true) or ?tracking_debug=true
 * - View: TrackingDebug.displayHistory() or window.TrackingDebug.getStoredEvents()
 * - Clear: TrackingDebug.clearHistory()
 * 
 * MODIFY THIS FILE FOR:
 * - Debug logging behavior
 * - Debug mode state management
 * 
 * DON'T MODIFY THIS FILE FOR:
 * - Feature tracking → tracking.js
 * - Core tracking → tracking-core.js
 */

const TrackingDebug = {
    STORAGE_KEY: 'tracking_debug_events',
    MAX_EVENTS: 42,
    
    /**
     * Debug mode - can be enabled/disabled via:
     * - localStorage.setItem('tracking_debug', 'true') / 'false'
     * - window.TRACKING_DEBUG = true / false
     * - URL parameter: ?tracking_debug=true
     */
    isEnabled() {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('tracking_debug') === 'true') return true;
        if (urlParams.get('tracking_debug') === 'false') return false;
        
        if (typeof window.TRACKING_DEBUG !== 'undefined') {
            return Boolean(window.TRACKING_DEBUG);
        }
        
        const stored = localStorage.getItem('tracking_debug');
        if (stored !== null) return stored === 'true';
        
        return false;
    },
    
    /**
     * Enable or disable debug mode
     * @param {boolean} enabled - Whether to enable debug mode
     */
    setDebugMode(enabled) {
        localStorage.setItem('tracking_debug', enabled ? 'true' : 'false');
        if (enabled) {
            this.displayHistory();
        } else {
            console.log('[Tracking Debug] Disabled');
        }
    },
    
    /**
     * Get stored events from localStorage
     * @returns {Array} Array of event objects
     */
    getStoredEvents() {
        try {
            const stored = localStorage.getItem(this.STORAGE_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch (e) {
            return [];
        }
    },
    
    /**
     * Store event in localStorage (keeps last MAX_EVENTS)
     * @param {object} eventData - Event data to store
     */
    storeEvent(eventData) {
        const events = this.getStoredEvents();
        events.push(eventData);
        
        // Keep only last MAX_EVENTS
        if (events.length > this.MAX_EVENTS) {
            events.splice(0, events.length - this.MAX_EVENTS);
        }
        
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(events));
        } catch (e) {
            console.warn('[Tracking Debug] Failed to store event:', e);
        }
    },
    
    /**
     * Clear stored events
     */
    clearHistory() {
        localStorage.removeItem(this.STORAGE_KEY);
        console.log('[Tracking Debug] History cleared');
    },
    
    /**
     * Debug logging for tracking events
     * Stores event in localStorage instead of logging to console
     * @param {string} tracker - Tracker name ('Amplitude' or 'GA4')
     * @param {string} eventName - Event name
     * @param {object} eventData - Event data/properties
     * @param {string} status - Status: 'sent', 'pending', or 'error'
     */
    log(tracker, eventName, eventData, status = 'sent') {
        if (!this.isEnabled()) return;
        
        const event = {
            timestamp: new Date().toISOString(),
            tracker,
            eventName,
            eventData,
            status,
            pageUrl: window.location.href,
            pageTitle: document.title
        };
        
        // Check tracker availability
        if (tracker === 'Amplitude') {
            event.amplitudeLoaded = typeof window.amplitude !== 'undefined' && window.amplitude.track;
        } else if (tracker === 'GA4') {
            event.dataLayerAvailable = typeof dataLayer !== 'undefined';
        }
        
        this.storeEvent(event);
    },
    
    /**
     * Display stored events in console (latest first)
     */
    displayHistory() {
        const events = this.getStoredEvents();
        
        if (events.length === 0) {
            console.log('[Tracking Debug] No events stored yet');
            return;
        }
        
        // Reverse to show latest events first
        const reversedEvents = [...events].reverse();
        
        console.group(`[Tracking Debug] Event History (${events.length} events, latest first)`);
        
        reversedEvents.forEach((event, index) => {
            const statusIcon = event.status === 'sent' ? '✓' : event.status === 'pending' ? '⏳' : '✗';
            const pageInfo = event.pageUrl !== window.location.href ? ` [${new URL(event.pageUrl).pathname}]` : '';
            
            console.group(`${index + 1}. ${statusIcon} [${event.tracker}] ${event.eventName}${pageInfo}`);
            console.log('Timestamp:', event.timestamp);
            console.log('Page:', event.pageUrl);
            console.log('Status:', event.status);
            
            if (event.amplitudeLoaded !== undefined) {
                console.log('Amplitude Loaded:', event.amplitudeLoaded);
            }
            if (event.dataLayerAvailable !== undefined) {
                console.log('dataLayer Available:', event.dataLayerAvailable);
            }
            
            console.log('Event Data:', event.eventData);
            console.groupEnd();
        });
        
        console.log('\nTo clear history: TrackingDebug.clearHistory()');
        console.log('To view raw data: TrackingDebug.getStoredEvents()');
        console.groupEnd();
    }
};

// Auto-display history on page load if debug is enabled
if (typeof window !== 'undefined' && TrackingDebug.isEnabled()) {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            TrackingDebug.displayHistory();
        });
    } else {
        TrackingDebug.displayHistory();
    }
}

// Expose globally for easy console access
if (typeof window !== 'undefined') {
    window.TrackingDebug = TrackingDebug;
}

export default TrackingDebug;
